"use client";

import { memo, useLayoutEffect, useMemo, useRef, useState, type FC, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { FolderIcon, ChevronDownIcon, FileIcon, PlusIcon, XIcon } from "lucide-react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import { type ThreadDocument } from "@/components/custom/contexts/threadContext";

// ─── Extension color system ──────────────────────────────────────────────────

type ExtTint = { bg: string; fg: string; label: string };

const EXT_TINTS: Record<string, ExtTint> = {
  pdf:  { bg: "#fef0ec", fg: "#9a2e23", label: "P" },
  doc:  { bg: "#eaf0fb", fg: "#1f4ecf", label: "W" },
  docx: { bg: "#eaf0fb", fg: "#1f4ecf", label: "W" },
  xls:  { bg: "#e0f0e2", fg: "#2d7a47", label: "X" },
  xlsx: { bg: "#e0f0e2", fg: "#2d7a47", label: "X" },
  csv:  { bg: "#e0f0e2", fg: "#2d7a47", label: "X" },
  png:  { bg: "#ece2fa", fg: "#6c44b5", label: "IMG" },
  jpg:  { bg: "#ece2fa", fg: "#6c44b5", label: "IMG" },
  jpeg: { bg: "#ece2fa", fg: "#6c44b5", label: "IMG" },
  gif:  { bg: "#ece2fa", fg: "#6c44b5", label: "IMG" },
  webp: { bg: "#ece2fa", fg: "#6c44b5", label: "IMG" },
  txt:  { bg: "#f0f0f0", fg: "#555555", label: "T" },
  md:   { bg: "#f0f0f0", fg: "#555555", label: "T" },
};

/** Prefer the backend-resolved `type` (Praetor MCP "type" field or filename-extension
 *  fallback computed server-side). Fall back to client-side filename parsing for any
 *  doc that came in before the wire field landed (e.g. pre-creation pending picks).
 *  Returns null when no extension can be determined — caller renders a generic icon. */
function typeOf(doc: ThreadDocument): string | null {
  const fromWire = doc.type?.trim().toLowerCase();
  if (fromWire) return fromWire;
  if (!doc.name) return null;
  const dot = doc.name.lastIndexOf(".");
  if (dot < 0 || dot === doc.name.length - 1) return null;
  const ext = doc.name.slice(dot + 1).toLowerCase();
  return ext.length >= 1 && ext.length <= 5 ? ext : null;
}

/** Returns the colored stamp tint for a known extension, or null when unknown — caller
 *  renders a neutral file icon instead of the legacy "?" fallback. */
function tintFor(ext: string | null): ExtTint | null {
  if (!ext) return null;
  return EXT_TINTS[ext] ?? null;
}

// ─── Stamp cluster (top-N extension tiles, overlapping) ──────────────────────

function useTopExtensions(docs: ThreadDocument[], max: number): ExtTint[] {
  return useMemo(() => {
    // Only count docs whose type is known — unknown types are represented as a
    // single Briefcase fallback by the case-pill renderer, not as a "?" stamp.
    const counts = new Map<string, number>();
    for (const d of docs) {
      const tint = tintFor(typeOf(d));
      if (!tint) continue;
      counts.set(tint.label, (counts.get(tint.label) ?? 0) + 1);
    }
    const sorted = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, max);

    const result: ExtTint[] = [];
    const seen = new Set<string>();
    for (const d of docs) {
      const tint = tintFor(typeOf(d));
      if (!tint) continue;
      if (!seen.has(tint.label) && sorted.some(([l]) => l === tint.label)) {
        result.push(tint);
        seen.add(tint.label);
      }
      if (result.length >= max) break;
    }
    return result;
  }, [docs, max]);
}

/** Renders the case-pill leading marker. When at least one doc has a known type,
 *  shows the stacked stamp cluster (e.g. P + W). When no type is known across the
 *  whole spis (typical for Praetor case-doc IDs without extensions), shows a single
 *  Briefcase icon as the generic case marker. */
const CaseLeadingMarker: FC<{ tints: ExtTint[] }> = ({ tints }) => {
  if (tints.length === 0) {
    return (
      <span
        className="flex items-center justify-center shrink-0 text-[#4a4d57]"
        style={{ width: 18, height: 18, marginRight: 2 }}
      >
        <FolderIcon className="size-[14px]" />
      </span>
    );
  }
  return (
    <span className="flex items-center" style={{ marginRight: 2 }}>
      {tints.map((t, i) => (
        <span
          key={t.label}
          className="flex items-center justify-center rounded-[4px] border-[1.5px] border-white text-[8px] font-bold leading-none select-none"
          style={{
            width: 18,
            height: 18,
            backgroundColor: t.bg,
            color: t.fg,
            marginLeft: i > 0 ? -9 : 0,
            fontFamily: "'JetBrains Mono', monospace",
            zIndex: tints.length - i,
            position: "relative",
          }}
        >
          {t.label}
        </span>
      ))}
    </span>
  );
};

// ─── Single extension tile for doc rows ──────────────────────────────────────

/** Per-doc-row leading icon. Known type → colored extension stamp; unknown → neutral
 *  outline file icon (replaces the legacy "?" tile from when types weren't plumbed). */
const ExtTile: FC<{ doc: ThreadDocument }> = ({ doc }) => {
  const t = tintFor(typeOf(doc));
  if (!t) {
    return (
      <span
        className="flex items-center justify-center shrink-0 text-muted-foreground"
        style={{ width: 18, height: 18 }}
      >
        <FileIcon className="size-[14px]" />
      </span>
    );
  }
  return (
    <span
      className="flex items-center justify-center rounded-[4px] text-[8px] font-bold leading-none select-none shrink-0"
      style={{
        width: 18,
        height: 18,
        backgroundColor: t.bg,
        color: t.fg,
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      {t.label}
    </span>
  );
};

// ─── CasePill ────────────────────────────────────────────────────────────────

export interface CasePillProps {
  name: string;
  /** Optional human case number (e.g. "12345/2025"). When present, rendered
   *  inline after the case name in a muted monospace style. */
  caseNumber?: string;
  docs: ThreadDocument[];
  primary?: boolean;
  onRemoveDoc: (docId: string) => void;
  onOpenDoc: (docId: string) => void;
  onRemoveSpis?: () => void;
  /** Open the document picker pre-focused on this case. When undefined the
   *  per-pill `+` is hidden (e.g. a "loose docs" pill with no caseId). */
  onAddDocs?: () => void;
  /** When set (by a parent CasePillRow during compaction), the pill is forced
   *  to this width and text truncates. When undefined the pill sizes to content. */
  maxWidth?: number;
}

export const CasePill: FC<CasePillProps> = memo(function CasePill({
  name,
  caseNumber,
  docs,
  primary = false,
  onRemoveDoc,
  onOpenDoc,
  onRemoveSpis,
  onAddDocs,
  maxWidth,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const topTints = useTopExtensions(docs, 3);
  const showCountBadge = docs.length > 0;

  const handleAddDocsClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onAddDocs?.();
  };

  const handleRemoveClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemoveSpis?.();
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="listbox"
          title={caseNumber ? `${caseNumber} — ${name}` : name}
          className={cn(
            "group inline-flex items-center rounded-full border bg-white transition-colors select-none cursor-pointer",
            "hover:bg-[#f4f4f0] focus-visible:bg-[#f4f4f0] focus-visible:outline-none",
          )}
          style={{
            height: 32,
            paddingLeft: 10,
            paddingRight: 6,
            gap: 10,
            borderColor: "#e8e8e3",
            ...(maxWidth ? { width: maxWidth, minWidth: maxWidth } : {}),
          }}
        >
          {/* Leading icon */}
          <span className="relative inline-flex shrink-0" style={{ color: "#6a6d76" }}>
            <CaseLeadingMarker tints={topTints} />
          </span>

          {/* Text: name + caseNumber on same baseline */}
          <span
            className="inline-flex items-baseline flex-1 min-w-0"
            style={{ gap: 6 }}
          >
            <span
              className="truncate leading-none"
              style={{ fontSize: 13, fontWeight: 500, color: "#14151a" }}
            >
              {name}
            </span>
            {caseNumber && (
              <span
                className="truncate leading-none shrink-0"
                style={{
                  fontSize: 11,
                  color: "#9a9da6",
                  fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                  whiteSpace: "nowrap",
                }}
              >
                {caseNumber}
              </span>
            )}
          </span>

          {/* Count badge */}
          {showCountBadge && (
            <span
              className="flex items-center justify-center rounded-full leading-none select-none shrink-0"
              style={{
                backgroundColor: "#f4f4f0",
                color: "#4a4d57",
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 7px",
                fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              {docs.length}
            </span>
          )}

          {/* Caret */}
          <ChevronDownIcon
            className={cn(
              "shrink-0 transition-transform",
              open && "rotate-180",
            )}
            style={{ width: 11, height: 11, color: "#b9bcc4" }}
          />

          {/* Add-docs button */}
          {onAddDocs && (
            <span
              role="button"
              aria-label={t("composer.casePill.addDocsTooltip")}
              title={t("composer.casePill.addDocsTooltip")}
              tabIndex={0}
              onClick={handleAddDocsClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onAddDocs();
                }
              }}
              className="inline-flex items-center justify-center rounded-full cursor-pointer shrink-0 hover:bg-[#f4f4f0] transition-colors"
              style={{ width: 22, height: 22, color: "#6a6d76" }}
            >
              <PlusIcon style={{ width: 14, height: 14 }} />
            </span>
          )}

          {/* Separator + close button */}
          {onRemoveSpis && (
            <>
              <span
                aria-hidden
                className="shrink-0"
                style={{ width: 1, height: 14, backgroundColor: "#ececea", margin: "0 2px" }}
              />
              <span
                role="button"
                aria-label={t("composer.casePill.removeSpis")}
                title={t("composer.casePill.removeSpis")}
                tabIndex={0}
                onClick={handleRemoveClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemoveSpis();
                  }
                }}
                className="inline-flex items-center justify-center rounded-full cursor-pointer shrink-0 hover:bg-[#f4f4f0] transition-[background,color]"
                style={{ width: 22, height: 22, color: "#9a9da6" }}
              >
                <XIcon style={{ width: 11, height: 11 }} />
              </span>
            </>
          )}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={8}
          className="z-50 rounded-[14px] border bg-popover outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{
            borderColor: "#e8e8e3",
            boxShadow: "0 12px 32px -10px rgba(0,0,0,.14)",
            padding: 6,
            minWidth: 280,
            maxWidth: 360,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-2.5 pt-1.5 pb-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[13px] font-medium text-foreground truncate">
                {name}
              </span>
              {primary && (
                <span className="shrink-0 rounded-md bg-[#14151a] px-1.5 py-0.5 text-[9px] font-medium text-white uppercase tracking-wide">
                  {t("composer.casePill.primaryBadge")}
                </span>
              )}
            </div>
            {onRemoveSpis && (
              <button
                type="button"
                onClick={onRemoveSpis}
                title={t("composer.casePill.removeSpis")}
                className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer shrink-0 ml-2"
              >
                <XIcon className="size-3.5" />
              </button>
            )}
          </div>

          {/* Document list */}
          <div
            className="max-h-56 overflow-y-auto [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none" }}
          >
            {docs.length === 0 ? (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                {t("documents.noneSelected")}
              </div>
            ) : (
              docs.map((doc) => (
                <DocRow
                  key={doc.docId}
                  doc={doc}
                  onRemove={onRemoveDoc}
                  onOpen={onOpenDoc}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {docs.length > 0 && (
            <div
              className="flex items-center justify-between border-t px-2.5 py-1.5 mt-1"
              style={{ borderColor: "#e8e8e3", backgroundColor: "#fbfbf8", borderRadius: "0 0 14px 14px" }}
            >
              <span className="text-[11px] text-muted-foreground">
                {t("composer.casePill.docCount", { count: docs.length })}
              </span>
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});

// ─── CasePillRow: dynamic equal-truncation ───────────────────────────────────
// Holds the row of CasePill instances and decides per-render whether each pill
// is natural-width or compacted to equal width. The "natural" measurement uses
// a hidden mirror row rendered at intrinsic width (offscreen, aria-hidden,
// pointer-events-none); the visible row reads `maxWidth` from the result.
//
// Why a mirror row instead of just measuring scrollWidth of the visible row:
// once the visible row is constrained, its scrollWidth equals clientWidth and
// we'd lose the natural-width signal. The mirror is unconstrained, always.
// Cost: two React subtrees per pill (popover triggers + buttons), but the
// mirror is non-interactive — the cost is one extra DOM tree per render, not
// a functional duplication.

const GAP_PX = 6; // tailwind gap-1.5 = 0.375rem = 6px
const MIN_PILL_WIDTH = 110; // single-row pill has more fixed-width elements

export interface CasePillRowItem extends Omit<CasePillProps, "maxWidth"> {
  /** Stable key for React reconciliation (typically the caseId or a synthetic id). */
  key: string;
}

export const CasePillRow: FC<{ items: CasePillRowItem[] }> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);

  // Identity-stable signature of the pill list so the effect's dep array detects
  // additions / removals / order changes / case-name resolves without rerunning
  // on every render of a stable list.
  const signature = items.map(p => `${p.key}|${p.caseNumber ?? ""}|${p.name}|${p.docs.length}`).join("§");

  useLayoutEffect(() => {
    const c = containerRef.current;
    const m = mirrorRef.current;
    if (!c || !m) return;

    const measure = () => {
      const available = c.clientWidth;
      const intrinsic = m.scrollWidth;
      if (items.length === 0) {
        setMaxWidth(null);
        return;
      }
      // +1 tolerance for sub-pixel rounding — avoids flicker on exact boundary.
      if (intrinsic <= available + 1) {
        setMaxWidth(null);
        return;
      }
      const gapTotal = Math.max(0, items.length - 1) * GAP_PX;
      const per = Math.floor((available - gapTotal) / items.length);
      setMaxWidth(Math.max(MIN_PILL_WIDTH, per));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(c);
    return () => ro.disconnect();
    // signature drives recomputation when pill identity / contents change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  if (items.length === 0) return null;

  return (
    <>
      {/* Visible row — single line, no wrap. Compaction kicks in via maxWidth. */}
      <div
        ref={containerRef}
        className="flex flex-1 min-w-0 items-center gap-1.5 overflow-hidden"
      >
        {items.map(({ key, ...rest }) => (
          <CasePill key={key} {...rest} maxWidth={maxWidth ?? undefined} />
        ))}
      </div>
      {/* Mirror row — offscreen, intrinsic widths. Used only for measurement. */}
      <div
        ref={mirrorRef}
        aria-hidden
        className="flex items-center gap-1.5 absolute pointer-events-none"
        style={{ left: -9999, top: -9999, visibility: "hidden" }}
      >
        {items.map(({ key, ...rest }) => (
          <CasePill key={`${key}__mirror`} {...rest} />
        ))}
      </div>
    </>
  );
};

// ─── Document row inside popover ─────────────────────────────────────────────

const DocRow: FC<{
  doc: ThreadDocument;
  onRemove: (docId: string) => void;
  onOpen: (docId: string) => void;
}> = ({ doc, onRemove, onOpen }) => {
  const { t } = useTranslation();
  const displayName =
    doc.name === undefined ? t("documents.loading")
    : doc.name === ""       ? t("documents.outerCaseDocument")
    :                         doc.name;

  return (
    <div
      className="group flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-[#f4f4f0] transition-colors"
    >
      <ExtTile doc={doc} />
      <span
        onClick={() => onOpen(doc.docId)}
        title={t("documents.openPreview")}
        className="text-[13px] text-foreground truncate flex-1 cursor-pointer"
      >
        {displayName}
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(doc.docId); }}
        title={t("composer.removeSource", { name: displayName })}
        aria-label={t("composer.removeSource", { name: displayName })}
        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity cursor-pointer shrink-0"
      >
        <XIcon className="size-3" />
      </button>
    </div>
  );
};
